import { useMutation } from "@apollo/client";
import { DotsHorizontalIcon } from "@heroicons/react/solid";
import moment from "moment";
import { useCallback } from "react";
import {
  RelinkMergeDevIntegrationMutation,
  RelinkMergeDevIntegrationMutationVariables,
} from "types/graphql-schema";

import { errorNotificationVar } from "@cache/cache";
import Dropdown from "@components/dropdown/dropdown";
import { getAssetUrl } from "@helpers/helpers";

import relinkMergeDevIntegrationMutation from "../../graphql/relink-merge-dev-integration";
import { MERGE_CATEGORY_DESCRIPTIONS } from "./integrations-constants";
import { ConnectedIntegration, MergeIntegration } from "./integrations-types";
import { useMergeIntegrationLink } from "./use-merge-integration-link";

const IntegrationsConnectedTable = ({
  integrations,
  onDisconnect,
  onRefetch,
}: {
  integrations: ConnectedIntegration[];
  onDisconnect: (integration: ConnectedIntegration) => void;
  onRefetch: () => void;
}) => {
  // ---------------------------------------------------------------------------
  // Merge.dev relink flow
  // ---------------------------------------------------------------------------
  const [relinkMergeDevIntegration] = useMutation<
    RelinkMergeDevIntegrationMutation,
    RelinkMergeDevIntegrationMutationVariables
  >(relinkMergeDevIntegrationMutation);

  const { startLink } = useMergeIntegrationLink({
    onSuccess: () => {
      onRefetch();
    },
    successMessage: "Integration re-linked successfully",
  });

  const relinkMergeIntegration = useCallback(
    async (integration: MergeIntegration) => {
      try {
        const result = await relinkMergeDevIntegration({
          variables: {
            integrationId: integration.id,
          },
        });

        if (result.data?.relinkMergeDevIntegration?.success) {
          const linkToken = result.data.relinkMergeDevIntegration.linkToken;
          const originId =
            result.data.relinkMergeDevIntegration.endUserOriginId;
          if (linkToken && originId) {
            startLink(linkToken, originId);
          }
        } else {
          errorNotificationVar({
            title: "Error",
            description:
              result.data?.relinkMergeDevIntegration?.error ||
              "Failed to create re-link token",
          });
        }
      } catch (error) {
        errorNotificationVar({
          title: "Error",
          description: `Failed to create re-link token. ${error}`,
        });
      }
    },
    [relinkMergeDevIntegration, startLink],
  );

  if (integrations.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Active Integrations
        </h3>
        <span className="text-sm text-gray-600 px-3 py-1 bg-gray-100 border border-gray-300 rounded-full">
          {integrations.length}
        </span>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Integration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Synced
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {integrations.map((integration) => (
              <tr key={`${integration.type}-${integration.id}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 flex-shrink-0 border rounded p-1 flex items-center justify-center bg-white">
                      <img
                        src={getAssetUrl(integration.logo)}
                        alt={integration.name}
                        className="h-8 w-8 object-contain"
                      />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {integration.name}
                      </div>
                      {integration.type === "merge" && (
                        <div className="text-xs text-gray-500 capitalize">
                          {integration.category}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-500">
                    {integration.type === "native"
                      ? integration.description
                      : MERGE_CATEGORY_DESCRIPTIONS[integration.category] || ""}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {integration.connectedAt
                    ? moment(integration.connectedAt).fromNow()
                    : "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                  <Dropdown
                    align="end"
                    options={[
                      ...(integration.type === "native" &&
                      integration.configureUrl
                        ? [
                            {
                              type: "link" as const,
                              label: "Configure",
                              to: integration.configureUrl,
                            },
                          ]
                        : []),
                      ...(integration.type === "merge"
                        ? [
                            {
                              label: "Re-link",
                              onClick: () =>
                                relinkMergeIntegration(integration),
                            },
                          ]
                        : []),
                      {
                        label: "Disconnect",
                        onClick: () => onDisconnect(integration),
                      },
                    ]}
                  >
                    <button className="flex items-center text-gray-400 rounded-full">
                      <DotsHorizontalIcon className="size-5" />
                    </button>
                  </Dropdown>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IntegrationsConnectedTable;
